import { Accordion, AccordionDetails, AccordionSummary, Divider, Grid, Table, TableBody, TableCell, TableContainer, TableRow, Typography } from '@mui/material';
import { Box } from '@mui/system';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ApplicantEmploymentHistoryV2Dto, ApplicantRentalHistoryV2Dto, ApplicantV2Dto, ApplicationV2Dto, EmploymentStatusEnum } from '@inosio/payments-sdk';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import NumberFormat from 'react-number-format';
import { formatE164PhoneNumber, getDurationBetweenTwoDates } from '../../../../shared/util/formaters';
import ApplicationDetailsApplicantEntityData, { ApplicantEntityDataTableField, ApplicantEntityDataTableFieldType } from './ApplicationDetailsApplicantEntityData';
import { handleBuildCustomQuestionDataTable } from './helpers';
import { convertSqlDateToUSCalendarDate } from '../../../../shared/util/converters';

export interface ApplicationDetailsApplicantProps {
  application: ApplicationV2Dto;
}
export default function ApplicationDetailsApplicant(props: ApplicationDetailsApplicantProps) {
  const { id: applicantUuidParam } = useParams<{ id: string }>();
  const [thisApplicant, setThisApplicant] = useState<ApplicantV2Dto | undefined>();

  const handleBuildPersonalInfoDataTable = (thisApplicant: ApplicantV2Dto | undefined) => {
    const personInfoData: ApplicantEntityDataTableField[] = [
      {
        label: 'Full Name',
        value: (thisApplicant?.FirstName || '') + ' ' + (thisApplicant?.MiddleName || '') + ' ' + (thisApplicant?.LastName || ''),
      },
      {
        label: 'DOB',
        value: thisApplicant?.Dob ? convertSqlDateToUSCalendarDate(thisApplicant?.Dob) : '',
      },
      {
        label: 'SSN',
        value: thisApplicant?.Ssn || '(left blank)',
        fieldType: ApplicantEntityDataTableFieldType.Hidden,
      },
      {
        label: 'Gov ID Type',
        value: thisApplicant?.GovIdType === 'Drivers License' ? "Driver's License" : thisApplicant?.GovIdType || '',
      },
      {
        label: 'Gov ID Number',
        value: thisApplicant?.GovIdNumber || '',
      },
      {
        label: 'Phone',
        value: thisApplicant?.PhoneNumber ? formatE164PhoneNumber(thisApplicant?.PhoneNumber) : '',
      },
      {
        label: 'Email',
        value: thisApplicant?.Email || '',
      },
    ];

    const customQuestionsForPersonalInfo: ApplicantEntityDataTableField[] = handleBuildCustomQuestionDataTable(thisApplicant?.PersonalInfoCustomQuestionsAnswers || []);

    return [...personInfoData, ...(customQuestionsForPersonalInfo || [])];
  };

  const handleBuildAddressDataTable = (rentalHistory: ApplicantRentalHistoryV2Dto) => {
    const rentalHistoryTable: ApplicantEntityDataTableField[] = [
      {
        label: 'Occupancy Type',
        value: rentalHistory?.RentalHistoryType || '',
      },
      {
        label: 'Monthly Payment Amount',
        value: rentalHistory?.MonthlyPayment || '',
      },
      {
        label: 'Move In Date',
        value: rentalHistory?.MoveInDate ? convertSqlDateToUSCalendarDate(rentalHistory?.MoveInDate) : '',
      },
      {
        label: 'Move Out Date',
        value: rentalHistory?.MoveOutDate ? convertSqlDateToUSCalendarDate(rentalHistory?.MoveOutDate) : 'Current Address',
      },
      {
        label: rentalHistory?.RentalHistoryType === 'Owned' ? 'Bank / Lender' : 'Property Manager / Landlord',
        value: rentalHistory?.ContactFacilityName || '',
      },
      {
        label: rentalHistory?.RentalHistoryType === 'Owned' ? 'Purchase Date' : 'Property Manager / Landlord Phone',
        value:
          rentalHistory?.RentalHistoryType === 'Owned'
            ? rentalHistory?.PurchaseDate
              ? convertSqlDateToUSCalendarDate(rentalHistory?.PurchaseDate)
              : ''
            : rentalHistory?.ContactPhone
            ? formatE164PhoneNumber(rentalHistory?.ContactPhone)
            : '',
      },
      ...(rentalHistory?.RentalHistoryType !== 'Owned'
        ? [
            {
              label: 'Property Manager / Landlord Email',
              value: rentalHistory?.ContactEmail || '',
            },
          ]
        : []),
    ];

    const customQuestionsForThisAddress: ApplicantEntityDataTableField[] = handleBuildCustomQuestionDataTable(rentalHistory.CustomQuestionsAnswers || []);

    return [...rentalHistoryTable, ...(customQuestionsForThisAddress || [])];
  };

  const handleBuildEmploymentDataTable = (employment: ApplicantEmploymentHistoryV2Dto) => {
    const employmentHistoryTable: ApplicantEntityDataTableField[] = [
      {
        label: 'Type',
        value: employment?.Status || '',
      },
      ...(employment?.Status === EmploymentStatusEnum.Employed
        ? ([
            {
              label: 'Job Title',
              value: employment.Title || '',
            },
            {
              label: 'Company Name',
              value: employment?.CompanyName || '',
            },
            {
              label: 'Company Phone',
              value: employment?.CompanyPhone ? formatE164PhoneNumber(employment?.CompanyPhone) : '',
            },
            {
              label: 'Supervisor Name',
              value: employment?.CompanyContactName || '(left blank)',
            },
            {
              label: 'Supervisor Email',
              value: employment?.CompanyContactEmail || '(left blank)',
            },
            {
              label: 'Supervisor Phone',
              value: employment?.CompanyContactPhone ? formatE164PhoneNumber(employment?.CompanyContactPhone) : '(left blank)',
            },
            {
              label: 'Monthly Income',
              value: employment?.MonthlySalary || '',
            },
            {
              label: 'Start Date',
              value: employment?.StartDate ? convertSqlDateToUSCalendarDate(employment?.StartDate) : '',
            },
            {
              label: 'End Date',
              value: employment?.EndDate ? convertSqlDateToUSCalendarDate(employment?.EndDate) : 'Currently Employed',
            },
          ] as ApplicantEntityDataTableField[])
        : []),
      ...(employment?.Status === EmploymentStatusEnum.SelfEmployed
        ? ([
            {
              label: 'Monthly Inome',
              value: employment.MonthlySalary || '',
            },
            {
              label: 'Industry',
              value: employment?.Industry || '',
            },
            {
              label: 'Company Name',
              value: employment?.CompanyName || '',
            },
            {
              label: 'Company Address',
              value: `${employment.Address?.Line1 || ''} ${employment.Address?.Line2 || ''} ${employment.Address?.City ? employment.Address?.City + ',' : ''} ${employment.Address?.State || ''} ${
                employment.Address?.Zip || ''
              }`,
            },
            {
              label: 'Start Date',
              value: employment?.StartDate ? convertSqlDateToUSCalendarDate(employment?.StartDate) : '',
            },
            {
              label: 'End Date',
              value: employment?.EndDate ? convertSqlDateToUSCalendarDate(employment?.EndDate) : 'Currently Employed',
            },
          ] as ApplicantEntityDataTableField[])
        : []),
      ...(employment?.Status === EmploymentStatusEnum.NotEmployed
        ? ([
            {
              label: 'Avg Monthly Inome',
              value: employment.OtherIncomeAmount || '',
            },
            {
              label: 'Describe your other income',
              value: employment?.OtherIncomeDescription || '(left blank)',
            },
          ] as ApplicantEntityDataTableField[])
        : []),
    ];

    const customQuestionsForThisEmployment: ApplicantEntityDataTableField[] = handleBuildCustomQuestionDataTable(employment.CustomQuestionsAnswers || []);

    return [...employmentHistoryTable, ...(customQuestionsForThisEmployment || [])];
  };

  const handleBuildAdditionalInfoSection = (thisApplicant: ApplicantV2Dto | undefined) => {
    const customQuestionsForAdditionalInfo: ApplicantEntityDataTableField[] = handleBuildCustomQuestionDataTable(thisApplicant?.AdditionalCustomQuestionsAnswers || []);

    return customQuestionsForAdditionalInfo;
  };

  useEffect(() => {
    if (props.application && applicantUuidParam) {
      const thisApplicant =
        props.application.PrimaryApplicant.Uuid === applicantUuidParam ? props.application.PrimaryApplicant : props.application.CoApplicants.find((coap) => coap.Uuid === applicantUuidParam);
      setThisApplicant(thisApplicant);
      handleBuildPersonalInfoDataTable(thisApplicant);
    }
  }, [props.application, applicantUuidParam]);

  return (
    <Box
      sx={{
        width: '100%',
        overflow: 'auto',
        padding: 2,
        height: 'calc( 100vh - 294px )',
        '&::-webkit-scrollbar': {
          width: 7,
        },
        '&::-webkit-scrollbar-track': {
          boxShadow: `inset 0 0 6px rgba(0, 0, 0, 0.3)`,
          borderRadius: '24px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'darkgrey',
          borderRadius: '24px',
        },
      }}
    >
      <Typography sx={{ fontFamily: 'Work Sans !important', fontWeight: 'bold', marginBottom: 1 }}>Personal Info:</Typography>
      <Divider sx={{ marginY: 1, borderBottomWidth: 1, backgroundColor: 'black' }} />
      <ApplicationDetailsApplicantEntityData tableData={handleBuildPersonalInfoDataTable(thisApplicant)} attachments={thisApplicant?.GovIdAttachments || []} />
      <Divider sx={{ marginY: 1, borderBottomWidth: 1, backgroundColor: 'black' }} />
      <Typography sx={{ fontFamily: 'Work Sans !important', fontWeight: 'bold', marginBottom: 1, marginTop: 2 }}>Address History:</Typography>
      {thisApplicant?.RentalHistories.map((rh) => {
        return (
          <Accordion sx={{ marginTop: 2 }} elevation={0} key={rh.Uuid}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ marginTop: 2, boxShadow: 'none', '& .MuiPaper-root': { boxShadow: 'none' } }}>
              <Box sx={{ width: '100%' }}>
                <Typography sx={{ fontFamily: 'Work Sans !important' }}>{rh.IsCurrentAddress || !rh.MoveOutDate ? 'Current Address' : 'Former Address'}</Typography>
                <Grid container sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Grid item xs={4} sx={{ pl: 2 }}>
                    <Typography sx={{ fontFamily: 'Work Sans !important' }}>{rh.Address?.Line1 || ''}</Typography>
                    <Typography sx={{ fontFamily: 'Work Sans !important' }}>{rh.Address?.Line2 || ''}</Typography>
                    <Typography sx={{ fontFamily: 'Work Sans !important' }}>{`${rh.Address?.City ? rh.Address?.City + ',' : ''} ${rh.Address?.State || ''} ${rh.Address?.Zip || ''}`}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography sx={{ fontFamily: 'Work Sans !important' }}>
                      <NumberFormat
                        thousandsGroupStyle="thousand"
                        value={rh.MonthlyPayment}
                        prefix="$"
                        suffix="/month"
                        decimalSeparator="."
                        displayType="text"
                        type="text"
                        thousandSeparator={true}
                        allowNegative={true}
                        decimalScale={0}
                        fixedDecimalScale={true}
                        style={{ fontFamily: 'Work Sans !important' }}
                      />
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography sx={{ fontFamily: 'Work Sans !important' }}>{getDurationBetweenTwoDates(rh.MoveInDate, rh.MoveOutDate)}</Typography>
                  </Grid>
                </Grid>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <ApplicationDetailsApplicantEntityData tableData={handleBuildAddressDataTable(rh)} />
            </AccordionDetails>
          </Accordion>
        );
      })}

      <Divider sx={{ marginY: 1, borderBottomWidth: 1, backgroundColor: 'black' }} />
      <Typography sx={{ fontFamily: 'Work Sans !important', fontWeight: 'bold', marginBottom: 1, marginTop: 2 }}>Employment and Income:</Typography>
      {thisApplicant?.EmploymentHistories.map((eh) => {
        return (
          <Accordion sx={{ marginTop: 2 }} elevation={0} key={eh.Uuid}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ marginTop: 2, boxShadow: 'none', '& .MuiPaper-root': { boxShadow: 'none' } }}>
              <Grid container sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Grid item xs={4}>
                  <Typography sx={{ fontFamily: 'Work Sans !important' }}>{eh.Status}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography sx={{ fontFamily: 'Work Sans !important' }}>{eh.Title}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography sx={{ fontFamily: 'Work Sans !important' }}>
                    <NumberFormat
                      thousandsGroupStyle="thousand"
                      value={eh.MonthlySalary}
                      prefix="$"
                      decimalSeparator="."
                      displayType="text"
                      type="text"
                      suffix="/month"
                      thousandSeparator={true}
                      allowNegative={true}
                      decimalScale={0}
                      fixedDecimalScale={true}
                      style={{ fontFamily: 'Work Sans !important' }}
                    />
                  </Typography>
                </Grid>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <ApplicationDetailsApplicantEntityData tableData={handleBuildEmploymentDataTable(eh)} attachments={eh.Attachments || []} />
            </AccordionDetails>
          </Accordion>
        );
      })}
      <Divider sx={{ marginY: 1, borderBottomWidth: 1, backgroundColor: 'black' }} />
      <Typography sx={{ fontFamily: 'Work Sans !important', fontWeight: 'bold', marginBottom: 1, marginTop: 2 }}>Additional Info:</Typography>
      <Divider />
      <TableContainer>
        <Table size="small">
          <TableBody>
            {handleBuildAdditionalInfoSection(thisApplicant)?.map((row, index) => (
              <TableRow key={index}>
                <TableCell style={{ fontWeight: 'bold', width: '50%', verticalAlign: 'top', border: 'none' }}>
                  <Typography sx={{ fontFamily: 'Work Sans !important', fontWeight: 'bold', paddingLeft: row.paddingLeft }}>{row.label}:</Typography>
                </TableCell>
                <TableCell style={{ width: '50%', verticalAlign: 'top', border: 'none' }}>
                  <Typography sx={{ fontFamily: 'Work Sans !important' }}>{row.value}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
